import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import {
  Globe,
  Users,
  Shield,
  Zap,
  Heart,
  ArrowLeft,
  MapPin,
  Building2,
  Target,
  Sparkles,
} from "lucide-react";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";

export default function About() {
  const { t } = useTranslation();

  const stats = [
    { label: "Countries", value: "190+", icon: Globe },
    { label: "Active Users", value: "10M+", icon: Users },
    { label: "Listings", value: "50M+", icon: Building2 },
    { label: "Languages", value: "20+", icon: MapPin },
  ];

  const values = [
    {
      icon: Shield,
      title: "Safety First",
      description: "We prioritize user safety with advanced content moderation, identity verification, and partnerships with law enforcement.",
    },
    {
      icon: Globe,
      title: "Global Reach, Local Focus",
      description: "Connect with buyers and sellers in your neighborhood or expand your search globally across 190+ countries.",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Our modern platform is optimized for speed, letting you browse and post listings instantly.",
    },
    {
      icon: Heart,
      title: "Community Driven",
      description: "Built by the community, for the community. We listen to feedback and continuously improve.",
    },
  ];

  const team = [
    { name: "Alex Chen", role: "CEO & Co-Founder", image: "👨‍💼" },
    { name: "Sarah Miller", role: "CTO & Co-Founder", image: "👩‍💻" },
    { name: "Marcus Johnson", role: "Head of Safety", image: "👨‍🔬" },
    { name: "Elena Rodriguez", role: "Head of Design", image: "👩‍🎨" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <Navbar />

      <div className="container flex justify-between items-center h-12">
        <Link href="/">
          <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </a>
        </Link>
      </div>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
          <div className="container relative">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                About <span className="gradient-text">Listo Market</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                We're building the modern marketplace for everything. Our mission is to create 
                a safe, fast, and global platform that connects buyers and sellers worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-muted/30">
          <div className="container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <stat.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                  <div className="text-3xl font-bold mb-1">{stat.value}</div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16">
          <div className="container max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Story</h2>
              <div className="w-24 h-1 bg-primary mx-auto rounded-full" />
            </div>
            <div className="prose prose-lg dark:prose-invert mx-auto">
              <p className="text-muted-foreground leading-relaxed">
                Listo Market was founded in 2024 with a simple idea: create a modern classifieds 
                platform that puts safety and user experience first. We saw that existing platforms 
                were outdated, cluttered with spam, and often unsafe for users.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We set out to build something different. A platform that leverages modern technology 
                to create lightning-fast experiences, uses AI to keep users safe, and supports 
                communities around the world with localization in 20+ languages.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Today, Listo Market serves millions of users across 190+ countries, facilitating 
                billions of dollars in transactions every year. But we're just getting started. 
                Our vision is to become the world's most trusted marketplace, where anyone can 
                buy, sell, and connect safely.
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 bg-muted/30">
          <div className="container max-w-4xl">
            <div className="card-elegant p-8 md:p-12 text-center">
              <Target className="w-12 h-12 text-primary mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">Our Mission</h2>
              <p className="text-xl text-muted-foreground">
                To empower people everywhere to buy, sell, and connect safely and effortlessly, 
                creating economic opportunity and community connections across the globe.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Values</h2>
              <div className="w-24 h-1 bg-primary mx-auto rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {values.map((value) => (
                <div key={value.title} className="card-elegant p-6">
                  <value.icon className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16 bg-muted/30">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Leadership Team</h2>
              <div className="w-24 h-1 bg-primary mx-auto rounded-full" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {team.map((member) => (
                <div key={member.name} className="card-elegant p-6 text-center">
                  <div className="text-5xl mb-4">{member.image}</div>
                  <h3 className="font-semibold">{member.name}</h3>
                  <p className="text-sm text-muted-foreground">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join millions of users buying, selling, and connecting on Listo today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/browse">
                <a className="btn-secondary inline-flex items-center justify-center">
                  Browse Listings
                </a>
              </Link>
              <Link href="/post">
                <a className="btn-primary inline-flex items-center justify-center">
                  Post Your First Ad
                </a>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
